/**
 * PmTabs — bottom tab strip. Mirrors the legacy SWF tabs:
 *
 *   [Entrance(N)] [Multiplayer(N)] [Veterans(N)] [Subscribers(N)]
 *   [Garbage Bin(N)] [This Profile]   [@pm-tab]...
 *
 * Room tabs have coloured labels per the screenshots; the "This Profile"
 * tab only shows when a `?u=<username>` URL param is present (i.e. the
 * chat is embedded on someone's profile page). User counts come from
 * `state.users[<room>]` — only populated for rooms we've joined.
 */
import type { TabId } from '../hooks/useChat.js';
import { tabKey, STANDARD_ROOMS } from '../hooks/useChat.js';
import type { UserListEntry } from '../lib/types.js';
import { playUi } from '../lib/uiSound.js';

interface Props {
    active: TabId;
    pmList: string[];
    unread: Record<string, number>;
    /** Per-room user lists keyed by room name. */
    rooms: Record<string, UserListEntry[]>;
    /** Optional profile-scoped room from `?u=<username>`. */
    profileRoom: string | null;
    /** PM tabs that should be flashing right now (incoming alert). */
    flashPm?: Set<string>;
    /** Current user's rank (`k` value) — used to grey out gated rooms. */
    myRank?: number;
    onSelectRoom: (name: string) => void;
    onSelectPm: (other: string) => void;
    onClosePm: (other: string) => void;
}

/** Server-side gate hints (matches the legacy "You need to be a Veteran"
 *  error). The server is the final word; this is purely a UI lock-icon. */
const ROOM_REQUIRED_RANK: Record<string, number> = {
    // K enum: smaller k = higher rank. Veterans need k <= 15 (Veteran or above).
    veterans:    15,
    // Subscribers needed k <= 49 in legacy = a subscriber. We don't track sub
    // status — assume open to all logged-in users. Lock icon only if anon.
    subscribers: 16,
};

const ROOM_LABELS: Record<string, string> = {
    entrance:    'Entrance',
    multiplayer: 'Multiplayer',
    connect4:    'Connect4',
    yahtzee:     'Yahtzee',
    veterans:    'Veterans',
    subscribers: 'Subscribers',
    garbagebin:  'Garbage Bin',
};

const ROOM_COLORS: Record<string, string> = {
    // Entrance reads on a light tab background, so black/navy not white.
    entrance:    '#1a2540',
    multiplayer: '#c91e1e',     // red    (legacy)
    connect4:    '#c91e1e',     // red    (legacy — m0de screenshot)
    yahtzee:     '#b3860b',     // amber  (yellow was unreadable on light bg)
    veterans:    '#b3860b',     // amber
    subscribers: '#1a5dc0',     // blue
    garbagebin:  '#3e4a66',     // grey-slate
};

export function PmTabs(props: Props) {
    const { active, pmList, unread, rooms, profileRoom, flashPm, myRank, onSelectRoom, onSelectPm, onClosePm } = props;

    return (
        <div className="pm-tabs">
            {STANDARD_ROOMS.map(name => {
                const k = tabKey({ kind: 'room', name });
                const isActive = active.kind === 'room' && active.name === name;
                const count = rooms[name]?.length ?? 0;
                const joined = rooms[name] !== undefined;
                const reqRank = ROOM_REQUIRED_RANK[name];
                const locked  = reqRank !== undefined && myRank !== undefined && myRank > reqRank;
                return (
                    <button
                        key={name}
                        className={`pm-tab pm-tab-room ${isActive ? 'active' : ''} ${locked ? 'pm-tab-locked' : ''}`}
                        style={{ color: ROOM_COLORS[name] }}
                        onClick={() => { playUi('click'); onSelectRoom(name); }}
                        title={locked
                            ? `${ROOM_LABELS[name]} — you don't have access (rank-gated)`
                            : joined
                                ? `${ROOM_LABELS[name]} — ${count} users`
                                : `${ROOM_LABELS[name]} — click to join`}>
                        {locked && <span className="pm-tab-lock">🔒</span>}
                        {ROOM_LABELS[name]}{joined ? ` (${count})` : ''}
                        {!isActive && unread[k] ? <span className="pm-badge">{unread[k]}</span> : null}
                    </button>
                );
            })}
            {profileRoom && (() => {
                const k = tabKey({ kind: 'room', name: profileRoom });
                const isActive = active.kind === 'room' && active.name === profileRoom;
                const count = rooms[profileRoom]?.length ?? 0;
                const joined = rooms[profileRoom] !== undefined;
                return (
                    <button
                        key={profileRoom}
                        className={`pm-tab pm-tab-room ${isActive ? 'active' : ''}`}
                        style={{ color: '#ffffff' }}
                        onClick={() => onSelectRoom(profileRoom)}
                        title={`This Profile — ${profileRoom.replace(/^profile\//, '@')}`}>
                        This Profile{joined ? ` (${count})` : ''}
                        {!isActive && unread[k] ? <span className="pm-badge">{unread[k]}</span> : null}
                    </button>
                );
            })()}
            {pmList.map(other => {
                const t: TabId = { kind: 'pm', other };
                const k = tabKey(t);
                const isActive = active.kind === 'pm' && active.other === other;
                return (
                    <span key={other} className={`pm-tab pm-tab-pm ${isActive ? 'active' : ''} ${flashPm?.has(other) ? 'pm-tab-flash' : ''}`}>
                        <button className="pm-tab-name" onClick={() => { playUi('click'); onSelectPm(other); }}>
                            @ {other}
                            {!isActive && unread[k] ? <span className="pm-badge">{unread[k]}</span> : null}
                        </button>
                        <button className="pm-tab-close" onClick={() => onClosePm(other)} title="Close PM">×</button>
                    </span>
                );
            })}
        </div>
    );
}
